<template>
<div>
  <!--    Loader-->
  <loading-panel :loading="loading"></loading-panel>
  <!--  Breadcrumb-->
  <breadcrumb style="margin-left: -15px">
    <breadcrumb-item>
      <router-link to="/reports" exact>Report builder</router-link>
    </breadcrumb-item>
    <breadcrumb-item>
      <span>{{ report_name }}</span>
    </breadcrumb-item>
  </breadcrumb>

  <div class="row">
    <div class="col">
      <card>
        <h4 class="card-title">Edit report</h4>

        <div class="row">
          <div class="col-lg-4">
            <div class="row">
              <div class="col">
                <base-input label="Name" :error="errs.name" placeholder="Name" v-model="report.name"></base-input>
              </div>
            </div>

            <div class="row">
              <div class="col">
                <base-input label="Description">
                  <el-input
                    v-model="report.description"
                    :rows="10"
                    type="textarea"
                    placeholder="Description"
                  />
                </base-input>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <el-switch active-color="#e14eca"
                           v-model="report.is_date_range"  />
                <label> &nbsp;Show results for a given date range
                  <el-popover trigger="hover"
                              placement="right">

                    <div>
                      <div class="popover-body">
                        <span v-if="report.report_type_id == 1">
                          The report will show customers based on their <br/>initial interaction date with the company
                        </span>
                        <span v-if="report.report_type_id == 2">
                          The report will show events by event form submission date.
                        </span>
                        <span v-if="report.report_type_id == 3">
                          The report will show transactions by creation date.
                        </span>
                        <span v-if="report.report_type_id == 4">
                          The report will show purchases by purchase date.
                        </span>
                        <span v-if="report.report_type_id == 6">
                          The report will show payments by payment date.
                        </span>
                        <span v-if="report.report_type_id == 7">
                          The report will show promo codes by configured event date range.
                        </span>
                        <span v-if="report.report_type_id == 9">
                          The report will show project tickets by creation date.
                        </span>
                        <span v-if="report.report_type_id == 10">
                          The report will show time tracking entries by start date.
                        </span>
                        <span v-if="report.report_type_id == 11">
                          The report will compare each resource's staffed availability against the hours actually booked within the selected date range.
                        </span>
                        <span v-if="report.report_type_id == 12">
                          The report will show staff event involvement by event date.
                        </span>
                        <span v-if="report.report_type_id == 13">
                          The report will show waivers by signed date.
                        </span>
                        <span v-if="report.report_type_id == 14">
                          The report will show checklist runs by completion date. Runs still in progress use their start date.
                        </span>
                        <span v-if="report.report_type_id == 15">
                          The report will show checklist steps by their run's completion date.
                        </span>
                      </div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
            </div>

            <div class="row mt-3" v-if="report.is_date_range">
              <div class="col-lg-8">
                <base-input>
                  <el-select
                    class="select-primary"
                    v-model="report.time_period_type_id"
                    placeholder="Show results for"
                    value-key="id"
                  >
                    <el-option
                      class="select-primary"
                      v-for="(tpt, index) in time_periods"
                      :key="'IS' + index + j"
                      :label="tpt.name"
                      :value="{ name : tpt.name, id: tpt.id}"
                    >
                    </el-option>
                  </el-select>
                </base-input>
              </div>
            </div>

            <div class="row mb-3" v-if="report.is_date_range && report.hasOwnProperty('time_period_type_id')
             && report.time_period_type_id && report.time_period_type_id.id === 16">
              <div class="col">
                <base-input label="Start date" :error="errs.start_date">
                  <el-date-picker :editable="false"
                    type="date"
                    placeholder="mm-dd-yyyy"
                    value-format="yyyy-MM-dd"
                    :format="day_of_week + '' + date_format_cal"
                    name="date"
                    v-model="report.report_start_date" :picker-options="{ firstDayOfWeek: first_day_of_week }"
                  >
                  </el-date-picker>
                </base-input>
              </div>
              <div class="col">
                <base-input label="End date" :error="errs.end_date">
                  <el-date-picker :editable="false"
                    type="date"
                    placeholder="mm-dd-yyyy"
                    value-format="yyyy-MM-dd"
                    :format="day_of_week + '' + date_format_cal"
                    name="date"
                    v-model="report.report_end_date" :picker-options="{ firstDayOfWeek: first_day_of_week }"
                  >
                  </el-date-picker>
                </base-input>
              </div>
            </div>
            <hr style="border-top: 1px solid #f4f5f7;">
            <p class="text-uppercase">Report mode</p>
            <div class="row">
              <div class="col">
                <segmented-toggle
                  :value="report.is_summary ? 'summary' : 'detail'"
                  :options="[{value: 'detail', label: 'Plain report'}, {value: 'summary', label: 'Summary report'}]"
                  size="sm"
                  @input="v => report.is_summary = v === 'summary'"
                />
                <info-hint v-if="report.is_summary">{{ summaryHint }}</info-hint>
              </div>
            </div>
            <div class="row mt-2" v-if="report.is_summary">
              <div class="col-lg-8">
                <base-input label="Group results by" :error="errs.summary_group">
                  <el-select class="select-primary" v-model="report.summary_group_by_field_id" placeholder="Pick a checked field"
                             no-data-text="Check some fields first, then pick one here">
                    <el-option class="select-primary" v-for="f in groupableFieldOptions" :key="'GB' + f.key" :label="f.label" :value="f.key" />
                  </el-select>
                </base-input>
              </div>
            </div>
            <div class="row" v-if="report.is_summary && groupByFieldIsDate">
              <div class="col-lg-8">
                <base-input label="Group dates by">
                  <el-select class="select-primary" v-model="report.summary_bucket" placeholder="Bucket">
                    <el-option class="select-primary" v-for="b in bucketOptions" :key="b.value" :label="b.label" :value="b.value" />
                  </el-select>
                </base-input>
              </div>
            </div>
            <hr style="border-top: 1px solid #f4f5f7;">
            <p class="text-uppercase">Permissions</p>
            <div class="row">
              <div class="col">
                <el-switch active-color="#e14eca"
                           v-model="report.accessible_by_all_users"  />
                <label> &nbsp;Accessible to everyone
                  <el-popover trigger="hover"
                              placement="right">

                    <div>
                      <div class="popover-body">
                        When this setting is enabled, everyone with global <br/>permissions will be able to access this report. <br/>
                        Otherwise, it will be restricted to users you select.
                      </div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
            </div>

            <div class="row" v-if="!report.accessible_by_all_users">
              <div class="col">
                <base-input label="Viewers">
                  <el-select multiple
                             v-model="viewers"
                             class="select-primary"
                             name="email_group"
                             placeholder="Users"
                             value-key="id"
                  >
                    <el-option
                      v-for="(u, i) in users"
                      class="select-primary"
                      :label="getUserLabel(u)"
                      :value="u"
                      :key="'C' + i"
                    ></el-option>
                  </el-select>
                </base-input>
              </div>
            </div>
            <div class="row" v-if="!report.accessible_by_all_users">
              <div class="col">
                <base-input label="Editors">
                  <el-select multiple
                             v-model="editors"
                             class="select-primary"
                             name="email_group"
                             placeholder="Users"
                             value-key="id"
                  >
                    <el-option
                      v-for="(u, i) in users"
                      class="select-primary"
                      :label="getUserLabel(u)"
                      :value="u"
                      :key="'C' + i"
                    ></el-option>
                  </el-select>
                </base-input>
              </div>
            </div>
          </div>
          <div class="col">
            <div class="row">
              <div class="col-lg-3">
                <base-input label="Report type">
                  <b-list-group >
                    <b-list-group-item style="background-color: inherit; color: white" v-for="(rt, i) in report_types" :key="'RT' + i">
                      <base-radio v-on:input="setFieldData()" v-model="report.report_type_id" :name="rt.id.toString()" style="margin-top: 0">{{ gameToEventLabel(rt.name) }}</base-radio>
                    </b-list-group-item>
                  </b-list-group>
                </base-input>
              </div>

              <div class="col-lg-4">
                <label>Fields</label>
                <div class="fields-box">
                  <draggable
                    :list="field_options"
                    handle=".data-table__handle"
                    :fallback-tolerance="5"
                    @start="dragging = true"
                    @end="dragging = false"
                  >
                    <div class="row"
                         v-for="element in field_options"
                         :key="element.name"
                    >
                      <div class="col" >
                        <base-checkbox v-model="element.selected">{{ element.label }}
                        </base-checkbox>
                      </div>
                      <div class="col-auto mt-2" >
                        <span class="data-table__handle" aria-label="Drag to reorder"><i class="el-icon-rank"></i></span>
                      </div>
                    </div>
                  </draggable>
                </div>
              </div>

<!--              <div class="col" :key="t_key">-->
<!--                <base-input label="Fields">-->
<!--                  <el-transfer target-order="push" :titles="['Field options', 'Selected fields']" @change="handleTransferChange"-->
<!--                                v-model="report.fields" :data="field_options" />-->
<!--                </base-input>-->
<!--              </div>-->
            </div>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-lg-6">
            <base-button type="default" v-on:click="cancel()">Cancel</base-button>
            <base-button type="primary" class="ml-lg-2" v-on:click="saveReport()">Save</base-button>
          </div>
        </div>

      </card>
    </div>
  </div>


</div>
</template>

<script>
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import swal from "sweetalert2";
import {gameToEventLabel} from "@/util/eventLabels";
import {Switch, Transfer} from "element-ui";
import { BListGroupItem, BListGroup } from 'bootstrap-vue'
import _ from "lodash";
import draggable from 'vuedraggable'

export default {
  name: "EditReport",
  components: {
    BreadcrumbItem,
    Breadcrumb,
    BListGroupItem,
    BListGroup,
    [Transfer.name]: Transfer,
    [Switch.name]: Switch,
    draggable
  },
  data() {
    return {
      j: 0,
      time_periods: [],
      dragging: false,
      loading: false,
      errs: {
        name: '',
        start_date: '',
        end_date: '',
        summary_group: ''
      },
      report: {
        name: '',
        description: '',
        report_type_id: 1,
        fields: []
      },
      report_types: [],
      report_fields: [],
      field_options: [],
      selected_fields: [],
      report_name: '',
      day_of_week: '',
      date_format_cal: '',
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      first_day_of_week: JSON.parse(localStorage.getItem('group')).first_day_of_week,
      users: [],
      editors: [],
      viewers: []
    }
  },
  computed: {
    summaryHint() {
      const entities = {
        1: 'customers', 2: 'events', 3: 'transactions', 4: 'purchases', 6: 'payments',
        7: 'promo codes', 8: 'custom field responses', 9: 'project tickets',
        10: 'time tracking entries', 11: 'resources', 12: 'staff activity rows', 13: 'waivers', 14: 'checklist runs', 15: 'checklist steps'
      }
      const e = entities[parseInt(this.report.report_type_id)] || 'rows'
      return 'Instead of listing every row, this report will show one row per group of ' + e +
        ' with a count and totals. Check the fields to include on the right, then pick which one to group by below.'
    },
    groupableFieldOptions() {
      return this.field_options.filter(f => f.selected && f.data_type !== 'money')
    },
    groupByFieldIsDate() {
      const f = this.field_options.find(o => o.key === this.report.summary_group_by_field_id)
      return !!f && f.data_type === 'date'
    },
    bucketOptions() {
      return [
        { value: 'day', label: 'Day' },
        { value: 'week', label: 'Week' },
        { value: 'month', label: 'Month' },
        { value: 'quarter', label: 'Quarter' },
        { value: 'year', label: 'Year' }
      ]
    }
  },
  methods: {
    gameToEventLabel,
    getUserLabel(user){
      if(user.first_name && !user.last_name){
        return user.first_name
      } else if(!user.first_name && user.last_name){
        return user.last_name
      } else if(user.first_name && user.last_name){
        return user.first_name + ' ' + user.last_name
      }
      return user.email
    },
    handleTransferChange(value, direction, movedKeys){
      this.t_key++
    },
    cancel(){
      this.$router.push('/reports')
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    setFields(fields){
      let new_fields = []
      for(let n in fields){
        if(fields[n].selected){
          new_fields.push({
            id: fields[n].key
          })
        }
      }
      return new_fields
    },
    validate(){
      let errors = 0
      if(this.report.name === ''){
        this.errs.name = "Report name cannot be empty"
        errors++
      } else {
        this.errs.name = ''
      }

      if(this.report.is_date_range == true && this.report.time_period_type_id.id === 16
        && (this.report.report_start_date == '' || this.report.report_start_date === null)){
        this.errs.start_date = "Invalid start date"
        errors++
      } else {
        this.errs.start_date = ''
      }

      if(this.report.is_date_range == true && this.report.time_period_type_id.id === 16
        && (this.report.report_end_date == '' || this.report.report_end_date === null)){
        this.errs.end_date = "Invalid end date"
        errors++
      } else {
        this.errs.end_date = ''
      }

      if(this.report.is_summary && !this.groupableFieldOptions.find(f => f.key === this.report.summary_group_by_field_id)){
        this.errs.summary_group = "Pick a selected field to group by"
        errors++
      } else {
        this.errs.summary_group = ''
      }

      return errors > 0 ? false : true
    },
    mergePermissions(viewers, editors) {
      const allowed_users = {};

      for (const user of viewers) {
        if (!allowed_users[user.id]) {
          allowed_users[user.id] = { id: user.id, can_read: true, can_write: false };
        } else {
          allowed_users[user.id].can_read = true;
        }
      }

      for (const user of editors) {
        if (!allowed_users[user.id]) {
          allowed_users[user.id] = { id: user.id, can_read: false, can_write: true };
        } else {
          allowed_users[user.id].can_write = true;
        }
      }

      return Object.values(allowed_users);
    },
    async saveReport(){
      if(!this.validate()){
        return
      }
      if(!this.report.is_summary){
        this.report.summary_group_by_field_id = null
        this.report.summary_bucket = null
      }
      if(this.report.is_summary && !this.groupByFieldIsDate){
        this.report.summary_bucket = null
      }
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let reportId = this.$route.query.id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/reports/' + reportId

      let data = this.report

      data.allowed_users = this.mergePermissions(this.viewers, this.editors)
      this.report.fields = this.setFields(this.field_options)

      this.axios.put(url, data, config)
        .then(response => {
          this.loading = false
          swal('Success!', 'Report has been updated', 'success')
          this.$router.push('/reports')
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    setFieldData(){
      this.field_options = []
      for(let n in this.selected_fields) {
        if (parseInt(this.report.report_type_id) === this.selected_fields[n].report_type_id){
          this.field_options.push({
            key: this.selected_fields[n].report_field_id,
            label: this.gameToEventLabel(this.capitalizeFirstLetter(this.selected_fields[n].field.replaceAll('_', ' '))),
            data_type: this.selected_fields[n].data_type,
            selected: true
          })
        }
      }
      for(let n in this.report_fields){
        if(parseInt(this.report.report_type_id) === this.report_fields[n].report_type_id){
          if(!(_.find(this.selected_fields, { report_field_id: this.report_fields[n].id }))){
            this.field_options.push({
              key: this.report_fields[n].id,
              label: this.gameToEventLabel(this.capitalizeFirstLetter(this.report_fields[n].field.replaceAll('_', ' '))),
              data_type: this.report_fields[n].data_type,
              selected: false
            })
          }
        }
      }
    },
    capitalizeFirstLetter(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    async getReport(){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let reportId = this.$route.query.id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/reports/' + reportId

      this.axios.get(url, config)
        .then(response => {
          this.loading = false
          this.report = response.data.report
          this.users = response.data.users
          for(let n in response.data.allowed_users){
            if(response.data.allowed_users[n].can_read){
              this.viewers.push(response.data.allowed_users[n])
            }
            if(response.data.allowed_users[n].can_write){
              this.editors.push(response.data.allowed_users[n])
            }
          }
          this.report_types = response.data.report_types
          this.report_fields = response.data.report_fields
          this.selected_fields = response.data.selected_fields
          this.report_name = this.report.name
          this.time_periods = response.data.time_periods
          this.report.is_date_range = Boolean(this.report.is_date_range)
          this.report.accessible_by_all_users = Boolean(this.report.accessible_by_all_users)
          this.report.is_summary = Boolean(this.report.is_summary)
          this.report.summary_bucket = this.report.summary_bucket || 'month'
          this.report.time_period_type_id = _.find(this.time_periods, {id: this.report.time_period_type_id})

          this.setFieldData()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    }
  },
  mounted() {
    this.date_format_cal = this.date_format
    this.date_format_cal =  this.date_format_cal.replaceAll('Y', 'y')
    this.date_format_cal = this.date_format_cal.replaceAll('D', 'd')
    if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
      this.day_of_week = 'ddd, '
    }
    this.getReport()
  }
}
</script>

<style scoped>
.fields-box {
  border: 1px solid white;
  border-radius: 3px;
  padding: 5px 30px 15px 20px;
}
.list-group-item {
  border-color: white;
}
/* light mode: match .form-control border color */
.white-content .fields-box,
.white-content .list-group-item {
  border-color: rgba(29, 37, 59, 0.5);
}


</style>
